import { YesNoExperienceLevel } from "./types";
import type { IExperienceLevel, IExperienceLevelOption, WebformStep } from "./types";

export type CredentialExpirationStatus = "active" | "expiring_soon" | "expired";

export interface CredentialExpirationNotice {
    experienceLevel: string;
    expirationDate: string;
    status: Exclude<CredentialExpirationStatus, "active">;
    type: "Certification" | "License";
}

function parseDateOnly(value: string): {
    day: number;
    month: number;
    year: number;
} | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
        return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsedDate = new Date(Date.UTC(year, month - 1, day));

    if (
        parsedDate.getUTCFullYear() !== year ||
        parsedDate.getUTCMonth() !== month - 1 ||
        parsedDate.getUTCDate() !== day
    ) {
        return null;
    }

    return { day, month, year };
}

function formatDateOnly(year: number, month: number, day: number): string {
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
        2,
        "0"
    )}`;
}

export function formatDateInTimeZone(
    date: Date,
    timeZone = "America/New_York"
): string {
    const parts = new Intl.DateTimeFormat("en-US", {
        day: "2-digit",
        month: "2-digit",
        timeZone,
        year: "numeric",
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return `${values.year}-${values.month}-${values.day}`;
}

export function addCalendarMonths(date: string, months: number): string | null {
    const parsedDate = parseDateOnly(date);
    if (!parsedDate) {
        return null;
    }

    const monthIndex = parsedDate.month - 1 + months;
    const targetYear = parsedDate.year + Math.floor(monthIndex / 12);
    const targetMonthIndex = ((monthIndex % 12) + 12) % 12;
    const lastDayOfTargetMonth = new Date(
        Date.UTC(targetYear, targetMonthIndex + 1, 0)
    ).getUTCDate();

    return formatDateOnly(
        targetYear,
        targetMonthIndex + 1,
        Math.min(parsedDate.day, lastDayOfTargetMonth)
    );
}

export function getCredentialExpirationStatus(
    expirationDate?: string,
    today = new Date()
): CredentialExpirationStatus | undefined {
    if (!expirationDate || !parseDateOnly(expirationDate)) {
        return undefined;
    }

    const todayDate = formatDateInTimeZone(today);
    if (expirationDate < todayDate) {
        return "expired";
    }

    const expiringSoonDate = addCalendarMonths(todayDate, 3);
    if (expiringSoonDate && expirationDate <= expiringSoonDate) {
        return "expiring_soon";
    }

    return "active";
}

export function isExperienceCredentialExpired(
    experienceLevel?: IExperienceLevel[string],
    today = new Date()
): boolean {
    return (
        getCredentialExpirationStatus(
            experienceLevel?.credential_expiration_date,
            today
        ) === "expired"
    );
}

export function getCredentialExpirationNotices(
    experienceLevels: IExperienceLevel,
    experienceLevelOptions: IExperienceLevelOption[],
    today = new Date()
): CredentialExpirationNotice[] {
    const optionByValue = new Map(
        experienceLevelOptions.map((option) => [option.value, option])
    );

    return Object.entries(experienceLevels).flatMap(([experienceLevel, value]) => {
        const option = optionByValue.get(experienceLevel);
        const expirationDate = value.credential_expiration_date;
        const status = getCredentialExpirationStatus(expirationDate, today);
        if (!option || !expirationDate || status === "active" || !status) {
            return [];
        }

        const isLicense = option.category.toLowerCase().trim() === "licensing";
        const isActive = isLicense
            ? value.level === YesNoExperienceLevel.YES
            : option.certification_toogle &&
              value.certified === YesNoExperienceLevel.YES;
        if (!isActive) {
            return [];
        }

        return [
            {
                experienceLevel,
                expirationDate,
                status,
                type: isLicense ? "License" : "Certification",
            },
        ];
    });
}

export function isSameString(a?: string, b?: string) {
    return `${a}`.toLowerCase().trim() === `${b}`.toLowerCase().trim();
}

export function normalizeWebformStep(value: unknown): WebformStep | undefined {
    if (
        (typeof value !== "number" && typeof value !== "string") ||
        (typeof value === "string" && !/^[1-4]$/.test(value.trim()))
    ) {
        return undefined;
    }

    const step = Number(value);
    return Number.isInteger(step) && step >= 1 && step <= 4
        ? (step as WebformStep)
        : undefined;
}

export function getUniqueEmailRecipients(recipients: {
    to?: string;
    cc?: string[];
    bcc?: string[];
}): { to: string; cc: string[]; bcc: string[] } {
    const uniqueRecipients: { to: string; cc: string[]; bcc: string[] } = {
        to: recipients.to || "",
        cc: [],
        bcc: [],
    };

    // Add unique recipients to the appropriate category
    if (recipients.cc) {
        uniqueRecipients.cc = Array.from(new Set(recipients.cc));
    }
    if (recipients.bcc) {
        uniqueRecipients.bcc = Array.from(new Set(recipients.bcc));
    }

    // Remove common email addresses between 'cc' , 'to' and 'bcc'
    uniqueRecipients.cc = uniqueRecipients.cc.filter(
        (email) =>
            !uniqueRecipients.bcc.includes(email) &&
            !uniqueRecipients.to.includes(email)
    );

    uniqueRecipients.bcc = uniqueRecipients.bcc.filter(
        (email) =>
            !uniqueRecipients.cc.includes(email) &&
            !uniqueRecipients.to.includes(email)
    );

    return uniqueRecipients;
}

export const parseFirebaseTimestamp = (timestamp: any): Date | null => {
    try {
        if (!timestamp) {
            return null;
        }

        let date: Date;

        if (typeof timestamp === "object" && timestamp.toDate) {
            date = timestamp.toDate();
        } else {
            date = new Date(timestamp);
        }

        if (isNaN(date.getTime())) {
            console.warn("Invalid timestamp:", timestamp);
            return null;
        }

        return date;
    } catch (error) {
        console.error("Error parsing timestamp:", error);
        return null;
    }
};
